import type { TuningPatch, WebRtcOptimizationConfig } from '@playcastdotio/PeerCore';

/** Explicit prepared trial overrides join the existing pre-negotiation optimization path. */
export class TrialNativeConfiguration {
  apply(base: WebRtcOptimizationConfig, settings: TuningPatch): WebRtcOptimizationConfig {
    const next = { ...base };
    if (typeof settings['video.maxBitrateBps'] === 'number') {
      const bitrate = settings['video.maxBitrateBps']; next.maxBitrate = bitrate; next.burstMaxBitrate = bitrate;
      next.sdpBitrate = { minBitrate: Math.min(base.sdpBitrate?.minBitrate ?? 30, bitrate / 1_000),
        startBitrate: bitrate / 1_000, maxBitrate: bitrate / 1_000 }; next.burstSdpBitrate = next.sdpBitrate;
    }
    if (typeof settings['video.maxFramerate'] === 'number') next.targetFrameRate = settings['video.maxFramerate'];
    if (typeof settings['audio.maxBitrateBps'] === 'number') next.audioMaxBitrate = settings['audio.maxBitrateBps'];
    if (typeof settings['audio.stereo'] === 'boolean') next.audioStereo = settings['audio.stereo'];
    if (settings['video.temporalLayers'] === 'L1T1') next.temporalScalability = false;
    if (settings['video.temporalLayers'] === 'L1T3') next.temporalScalability = true;
    const preference = settings['video.degradationPreference'];
    if (preference === 'balanced' || preference === 'maintain-framerate' || preference === 'maintain-resolution') next.degradationPreference = preference;
    const codec = settings['video.codec'];
    if (codec === 'h264' || codec === 'h265') {
      next.codecPreferences = { ...base.codecPreferences, video: [codec === 'h264' ? 'H264' : 'H265'] };
      next.preferHevcFirst = codec === 'h265'; next.preferH264BitrateFloor = codec === 'h264';
    }
    return next;
  }

  transformSdp(sdp: string, settings: TuningPatch): string {
    const sections = sdp.split(/(?=m=)/);
    return sections.map(section => {
      if (section.startsWith('m=video ') && settings['video.nackRtx'] === 'disabled') {
        const lines = section.split(/\r?\n/), rtx = new Set<string>();
        for (const line of lines) { const match = /^a=rtpmap:(\d+) rtx\//i.exec(line); if (match) rtx.add(match[1]); }
        return lines.filter(line => {
          if (/^a=rtcp-fb:[\d*]+ nack$/.test(line)) return false;
          const payload = /^a=(?:rtpmap|fmtp|rtcp-fb):(\d+)\b/.exec(line);
          return !payload || !rtx.has(payload[1]);
        }).map(line => line.startsWith('m=video ') ? line.split(' ').filter((part, index) => index < 3 || !rtx.has(part)).join(' ') : line).join('\r\n');
      }
      if (section.startsWith('m=audio ') && typeof settings['audio.packetTimeMs'] === 'number') {
        const ptime = `a=ptime:${settings['audio.packetTimeMs']}`;
        return /^a=ptime:/m.test(section) ? section.replace(/^a=ptime:\d+/gm, ptime)
          : section.replace(/\r?\n$/, `\r\n${ptime}\r\n`);
      }
      return section;
    }).join('');
  }
}
